import { Component, inject } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { ActivatedRoute } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  ConfigResetActionDTO,
  ConfigResetStatus,
  RestoreTaskSummaryDTO,
  TaskProgressStatus,
  WebsiteRestoreStatus,
} from '../../../../shared/models/backups.models';
import moment from 'moment';
import { ConfigResetService } from '../../../../core/services/config-reset.service';
import { map, shareReplay } from 'rxjs';
import { WebsiteData } from '../../../../shared/models/website-overview.model';
import { AsyncPipe, DatePipe } from '@angular/common';
import { Status } from '../../../../shared/models/status-label.model';
import { StatusLabelComponent } from '../../../../shared/components/status-label/status-label.component';
import { TableComponent } from '../../../../shared/components/table/table.component';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { RestoreConfigDialogComponent } from '../../dialogs/restore-config-dialog/restore-config-dialog.component';
import { RestoreLogDialogComponent } from '../../dialogs/restore-log-dialog/restore-log-dialog.component';

@Component({
  selector: 'app-restore-details',
  imports: [
    MatCardModule,
    FontAwesomeModule,
    AsyncPipe,
    StatusLabelComponent,
    TableComponent,
    DatePipe,
    MatTableModule,
    MatTooltipModule,
    MatButtonModule,
  ],
  templateUrl: './restore-details.component.html',
  styleUrl: './restore-details.component.scss',
})
export class RestoreDetailsComponent {
  private dialog = inject(MatDialog);

  private readonly route = inject(ActivatedRoute);

  private readonly configRestoreService = inject(ConfigResetService);

  readonly restoreData: RestoreTaskSummaryDTO =
    this.route.snapshot.data['restoreData']['restoreTask'];

  readonly websiteData: WebsiteData = this.route.snapshot.data['restoreData']['websiteData'];

  configRestore$ = this.configRestoreService.getConfigResetByTask(this.restoreData.id).pipe(
    map((restores) =>
      restores.websiteRestores.find((r) => r.websiteId === this.websiteData.website.id),
    ),
    shareReplay(1),
  );

  snapshotRestore = this.restoreData.websiteRestores.find(
    (r) => r.websiteId === this.websiteData.website.id,
  );

  snapshotFiles = this.snapshotRestore?.websiteRestoreTargets.find(
    (t) => t.restoreBackupTarget?.type === 'vhost_document_root',
  );

  snapshotDatabases = this.snapshotRestore?.websiteRestoreTargets.filter(
    (t) => t.restoreBackupTarget?.type === 'database',
  );

  databasesNames = this.snapshotDatabases
    ? this.snapshotDatabases?.map((d) =>
        d.restoreBackupTarget ? d.restoreBackupTarget.uniqueIdentifier : 'unknown',
      )
    : [];

  showAllDatabases = false;

  get visibleDatabases(): string {
    return (this.showAllDatabases ? this.databasesNames : this.databasesNames.slice(0, 5)).join(
      ', ',
    );
  }

  get remainingDatabasesCount(): number {
    return Math.max(this.databasesNames.length - 5, 0);
  }

  snapshotMailboxes = this.snapshotRestore?.websiteRestoreTargets.filter(
    (t) => t.restoreBackupTarget?.type === 'mailbox',
  );

  mailboxesNames = this.snapshotMailboxes
    ? this.snapshotMailboxes?.map((m) =>
        m.restoreBackupTarget ? m.restoreBackupTarget.uniqueIdentifier : 'unknown',
      )
    : [];

  showAllMailboxes = false;

  get visibleMailboxes(): string {
    return (this.showAllMailboxes ? this.mailboxesNames : this.mailboxesNames.slice(0, 5)).join(
      ', ',
    );
  }

  get remainingMailboxesCount(): number {
    return Math.max(this.mailboxesNames.length - 5, 0);
  }

  readonly ACTION_NAMES = {
    reset_database: 'Database',
    reset_dns: 'DNS',
    reset_ftp: 'FTP',
    reset_mail: 'Mailbox',
    reset_ssl: 'SSL',
    reset_system_user: 'System user',
    reset_vhost: 'Origin VHost',
    reset_website: 'Website',
    reset_cdn: 'CDN Vhost',
    reset_redis: 'Redis',
  };

  readonly restoreSummary: {
    label: string;
    text: (value: RestoreTaskSummaryDTO) => string | undefined;
  }[] = [
    {
      label: 'Backup Date',
      text: (value: RestoreTaskSummaryDTO) =>
        value.backup ? moment(value.backup.createdAt).format('DD.mm.YYYY, hh:mm:ss') : 'N/A',
    },
    {
      label: 'Backup Type',
      text: (value: RestoreTaskSummaryDTO) =>
        value.backup?.scope === 'server' ? 'Server Backup' : 'Terminated Backup',
    },
    {
      label: 'Started At',
      text: (value: RestoreTaskSummaryDTO) =>
        moment(value.startDate).format('DD.mm.YYYY, hh:mm:ss'),
    },
    {
      label: 'Finished At',
      text: (value: RestoreTaskSummaryDTO) => moment(value.endDate).format('DD.mm.YYYY, hh:mm:ss'),
    },
  ];

  restoreConfigColumns = [
    {
      columnDef: 'id',
      header: 'Action ID',
      cell: (element: ConfigResetActionDTO) => element.id.toString(),
    },
    {
      columnDef: 'config',
      header: 'Config',
      cell: (element: ConfigResetActionDTO) => this.ACTION_NAMES[element.action],
    },
    {
      columnDef: 'status',
      header: 'Status',
      cell: () => '',
      useTemplate: true,
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  showConfig(action: ConfigResetActionDTO): void {
    this.dialog.open(RestoreConfigDialogComponent, {
      data: {
        subTitle: this.websiteData.website.domain,
        config: this.parseNestedJson(action.config),
      },
    });
  }

  showLogs(action: ConfigResetActionDTO): void {
    this.dialog.open(RestoreLogDialogComponent, {
      data: {
        subTitle: this.websiteData.website.domain,
        logs: action.logs,
        logsType: this.ACTION_NAMES[action.action],
      },
    });
  }

  toStatus(key: ConfigResetStatus): Status {
    switch (key) {
      case ConfigResetStatus.DONE:
        return Status.FINISHED;
      case ConfigResetStatus.PENDING:
        return Status.PENDING;
      default:
        return Status.FAILED;
    }
  }

  fromTaskStatus(key: TaskProgressStatus): Status {
    switch (key) {
      case TaskProgressStatus.Finished:
        return Status.FINISHED;
      case TaskProgressStatus.Pending:
        return Status.PENDING;
      case TaskProgressStatus.Running:
        return Status.IN_PROGRESS;
      default:
        return Status.FAILED;
    }
  }

  fromWebsiteRestoreStatus(key: WebsiteRestoreStatus): Status {
    switch (key) {
      case WebsiteRestoreStatus.Pending:
      case WebsiteRestoreStatus.RunningConfig:
      case WebsiteRestoreStatus.PendingTargets:
        return Status.ACTIVE;
      case WebsiteRestoreStatus.RunningTargets:
        return Status.IN_PROGRESS;
      case WebsiteRestoreStatus.Finished:
        return Status.FINISHED;
      default:
        return Status.FAILED;
    }
  }

  private parseNestedJson<T>(value: T): T {
    if (typeof value === 'string') {
      try {
        return this.parseNestedJson(JSON.parse(value));
      } catch {
        return value;
      }
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.parseNestedJson(item)) as T;
    }

    if (value !== null && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value).map(([key, child]) => [key, this.parseNestedJson(child)]),
      ) as T;
    }

    return value;
  }
}
